import { useMemo, useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { SrIcon } from "@/components/ui/SrIcon";
import { useCreateCustomer, type CustomerCreateSource } from "@/hooks/use-create-customer";
import { useCustomers } from "@/hooks/use-customers";
import { findDuplicateCustomer } from "@/lib/customer-duplicate";
import type { BrregHit } from "@/hooks/use-brreg-search";
import { FOREIGN_COUNTRIES } from "@/lib/countries";
import { CountryPicker } from "./CountryPicker";
import { BrregCompanyNameField } from "./BrregCompanyNameField";
import { DuplicateCustomerNotice } from "./DuplicateCustomerNotice";
import { NewCustomerCrmFields } from "./NewCustomerCrmFields";
import { useNewCustomerCrmFields } from "@/hooks/use-new-customer-crm-fields";

interface NewCustomerDialogProps {
  onCustomerCreated: (customerId: string) => void;
  /** Egen knapp som åpner dialogen. Default er ikon-knappen ved kundevelgeren. */
  trigger?: ReactNode;
  /**
   * Finnes kunden fra før, tilbyr dialogen «Åpne kundekortet» når denne er
   * satt (/kunder), ellers «Bruk denne kunden», som velger den eksisterende
   * kunden uten å forlate et halvutfylt prosjekt- eller tilbudsskjema.
   */
  onOpenExisting?: (customerId: string) => void;
  /** Hvor kunden ble opprettet, for event-loggen. */
  source?: CustomerCreateSource;
}

export function NewCustomerDialog({ onCustomerCreated, trigger, onOpenExisting, source }: NewCustomerDialogProps) {
  const [open, setOpen] = useState(false);
  const [brregLookupLoading, setBrregLookupLoading] = useState(false);
  const createCustomer = useCreateCustomer();
  const { data: customers = [] } = useCustomers();
  const crmFields = useNewCustomerCrmFields();
  const loading = createCustomer.isPending;
  const [isForeign, setIsForeign] = useState(false);
  const [countryCode, setCountryCode] = useState("");
  const [name, setName] = useState("");
  const [org, setOrg] = useState("");
  const [contact, setContact] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const [city, setCity] = useState("");

  const resetForm = () => {
    setIsForeign(false);
    setCountryCode("");
    setName("");
    setOrg("");
    setContact("");
    setEmail("");
    setPhone("");
    setAddress("");
    setPostalCode("");
    setCity("");
    crmFields.reset();
  };

  const handleBrregFetchByOrg = async (orgNr: string) => {
    if (isForeign) return;
    if (!orgNr.trim() || orgNr.replace(/\s/g, "").length !== 9) return;
    setBrregLookupLoading(true);
    try {
      const cleanOrgNr = orgNr.replace(/\s/g, "");
      const res = await fetch(`https://data.brreg.no/enhetsregisteret/api/enheter/${cleanOrgNr}`);
      if (!res.ok) {
        toast.error("Org.nr ikke funnet i Brønnøysundregisteret");
        return;
      }
      const data = await res.json();
      setName(data.navn || "");
      setAddress(data.forretningsadresse?.adresse?.[0] || "");
      setPostalCode(data.forretningsadresse?.postnummer || "");
      setCity(data.forretningsadresse?.poststed || "");
      if (data.hjemmeside) crmFields.setWebsite(data.hjemmeside);
      toast.success("Bedrift funnet");
    } catch (err) {
      toast.error(`Feil ved BRREG-søk: ${(err as Error).message}`);
    } finally {
      setBrregLookupLoading(false);
    }
  };

  const applyBrregHit = (hit: BrregHit) => {
    setName(hit.navn);
    setOrg(hit.organisasjonsnummer);
    setAddress(hit.adresse ?? "");
    setPostalCode(hit.postnummer ?? "");
    setCity(hit.poststed ?? "");
    crmFields.setWebsite(hit.hjemmeside ?? "");
  };

  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const countryValid = !isForeign || FOREIGN_COUNTRIES.some((c) => c.code === countryCode);
  const duplicate = useMemo(
    () => findDuplicateCustomer(customers, { organizationNumber: org, name, countryCode: isForeign ? countryCode : "NO" }),
    [customers, org, name, isForeign, countryCode],
  );
  const canCreate = name.trim().length > 0 && emailValid && countryValid && duplicate?.kind !== "org" && !crmFields.websiteInvalid && !loading;

  const closeWith = (customerId: string, action: (id: string) => void) => {
    action(customerId);
    setOpen(false);
    resetForm();
  };

  const handleCreate = () => {
    if (!canCreate) return;
    createCustomer.mutate(
      {
        name,
        organization_number: org.trim() || null,
        contact_person: contact || null,
        contact_email: email || null,
        contact_phone: phone || null,
        address: address || null,
        postal_code: postalCode || null,
        city: city || null,
        country_code: isForeign ? countryCode : "NO",
        source,
      },
      {
        onSuccess: ({ id }) => {
          crmFields.afterCreate(id);
          toast.success(`Kunde "${name}" opprettet`);
          closeWith(id, onCustomerCreated);
        },
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger ?? (
          <Button type="button" variant="outline" size="icon" className="shrink-0" aria-label="Ny kunde">
            <SrIcon name="plus" className="w-4 h-4" />
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Ny kunde</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Switch
              id="foreign-customer"
              checked={isForeign}
              onCheckedChange={(checked) => {
                setIsForeign(checked);
                setCountryCode("");
              }}
            />
            <Label htmlFor="foreign-customer">Utenlandsk kunde</Label>
          </div>

          {isForeign && (
            <div>
              <Label htmlFor="customer-country">Land *</Label>
              <CountryPicker
                id="customer-country"
                value={countryCode}
                onChange={setCountryCode}
              />
            </div>
          )}

          <div>
            <Label>Firmanavn *</Label>
            {isForeign ? (
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Firmanavn"
                autoComplete="off"
              />
            ) : (
              <>
                <BrregCompanyNameField
                  name={name}
                  onNameChange={setName}
                  onHitSelect={applyBrregHit}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Velg treff fra dropdown for å autofylle org.nr og adresse, eller
                  skriv inn firmanavn manuelt.
                </p>
              </>
            )}
          </div>
          <div>
            <Label>{isForeign ? "VAT-/org.nr (valgfritt)" : "Org.nr"}</Label>
            <div className="flex gap-2">
              <Input
                value={org}
                onChange={(e) => setOrg(e.target.value)}
                placeholder={isForeign ? "f.eks. SE5560525361" : "123 456 789"}
                onBlur={() => {
                  if (!isForeign && (org.trim().length === 9 || org.replace(/\s/g, "").length === 9)) {
                    handleBrregFetchByOrg(org);
                  }
                }}
              />
              {brregLookupLoading && <SrIcon name="loader2" className="w-4 h-4 animate-spin text-primary mt-2.5" />}
            </div>
          </div>
          {duplicate && (
            <DuplicateCustomerNotice
              duplicate={duplicate}
              actionLabel={onOpenExisting ? "Åpne kundekortet" : "Bruk denne kunden"}
              onAction={() => closeWith(duplicate.customer.id, onOpenExisting ?? onCustomerCreated)}
            />
          )}
          <div>
            <Label>Gateadresse</Label>
            <Input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Gatenavn 1"
            />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Postnr</Label>
              <Input
                value={postalCode}
                onChange={(e) => setPostalCode(e.target.value)}
                placeholder={isForeign ? "Postnr" : "0250"}
              />
            </div>
            <div className="col-span-2">
              <Label>Sted</Label>
              <Input
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder={isForeign ? "By" : "Oslo"}
              />
            </div>
          </div>
          <div>
            <Label>Kontaktperson</Label>
            <Input
              value={contact}
              onChange={(e) => setContact(e.target.value)}
              placeholder="Navn"
            />
          </div>
          <div>
            <Label>Telefon</Label>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder={isForeign ? "+46 …" : "+47 000 00 000"}
            />
          </div>
          <div>
            <Label>E-post *</Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="kontakt@firma.no"
              aria-invalid={email.trim().length > 0 && !emailValid}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Brukes som faktura-e-post i PowerOffice. Påkrevd for at
              fakturering skal fungere.
            </p>
          </div>
          <NewCustomerCrmFields fields={crmFields} />
          <Button
            className="w-full"
            onClick={handleCreate}
            disabled={!canCreate}
          >
            {loading && <SrIcon name="loader2" className="w-4 h-4 mr-2 animate-spin" />}
            Opprett kunde
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
